import React, { useState, useRef, useCallback, useEffect } from "react";
import { t as translate } from "../i18n/translations.js";

const API_BASE = "/api";
const MAX_DIMENSION = 1536; // px — higher res for better measurement estimation
const JPEG_QUALITY = 0.88;

const BR_DEFAULTS = {
  primaryColor: "#6366f1", secondaryColor: "#8b5cf6", accentColor: "#10b981",
  backgroundColor: "#f8f7ff", textColor: "#1a1a1a",
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  borderRadius: "16", buttonStyle: "gradient",
};
function br(branding) { return { ...BR_DEFAULTS, ...branding }; }

function useQuery() {
  return new URLSearchParams(window.location.search);
}

/**
 * Compress/resize an image to stay within Vercel's payload limit.
 * Forces portrait (3:4) orientation — crops landscape photos.
 * Accepts a base64 data URL, returns { b64, dataUrl }.
 */
function compressImage(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Ensure portrait orientation — crop landscape images
      const ratio = width / height;
      const targetRatio = 3 / 4;
      let srcX = 0, srcY = 0, srcW = width, srcH = height;

      if (ratio > targetRatio + 0.05) {
        // Image is landscape — crop sides to make portrait
        srcW = Math.round(height * targetRatio);
        srcX = Math.round((width - srcW) / 2);
        width = srcW;
      }

      // Scale down if needed
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * scale);
        height = Math.round(srcH * scale);
      } else {
        height = Math.round(srcH * (width / srcW));
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, width, height);
      const compressed = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
      resolve({ b64: compressed.split(",")[1], dataUrl: compressed });
    };
    img.onerror = () => {
      // Fallback: return original
      resolve({ b64: dataUrl.split(",")[1], dataUrl });
    };
    img.src = dataUrl;
  });
}

// Standalone mobile-first page — no Polaris, minimal CSS
const styles = {
  page: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "16px",
    minHeight: "100dvh",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#1a1a1a",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  header: {
    textAlign: "center",
    padding: "8px 0 16px",
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    margin: "4px 0 0",
    lineHeight: 1.4,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
    marginBottom: 12,
  },
  productImg: {
    width: "100%",
    maxHeight: 200,
    objectFit: "contain",
    borderRadius: 12,
    display: "block",
    margin: "0 auto",
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "#444",
    marginBottom: 6,
    display: "block",
  },
  cameraArea: {
    position: "relative",
    borderRadius: 16,
    overflow: "hidden",
    background: "#000",
    aspectRatio: "3/4",
    width: "100%",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transform: "scaleX(-1)",
  },
  photoPreview: {
    width: "100%",
    maxHeight: 350,
    objectFit: "contain",
    borderRadius: 12,
    display: "block",
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    padding: "16px 24px",
    fontSize: 16,
    fontWeight: 600,
    color: "#fff",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  },
  btnCapture: {
    position: "absolute",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    width: 72,
    height: 72,
    borderRadius: "50%",
    border: "4px solid #fff",
    background: "rgba(255,255,255,0.3)",
    backdropFilter: "blur(4px)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  },
  btnCaptureInner: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "#fff",
  },
  btnSecondary: {
    display: "block",
    width: "100%",
    padding: "14px 24px",
    fontSize: 15,
    fontWeight: 500,
    color: "#6366f1",
    background: "#f0f0ff",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    textAlign: "center",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  },
  btnText: {
    background: "none",
    border: "none",
    color: "#6366f1",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    padding: "8px 0",
    textAlign: "center",
    width: "100%",
    WebkitTapHighlightColor: "transparent",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 16,
    borderRadius: 12,
    border: "1.5px solid #ddd",
    outline: "none",
    boxSizing: "border-box",
    WebkitAppearance: "none",
  },
  error: {
    background: "#fef2f2",
    color: "#dc2626",
    padding: "12px 16px",
    borderRadius: 12,
    fontSize: 14,
    marginBottom: 12,
  },
  divider: {
    textAlign: "center",
    color: "#999",
    fontSize: 13,
    margin: "4px 0",
  },
  footer: {
    textAlign: "center",
    padding: "16px 0 8px",
    color: "#aaa",
    fontSize: 12,
    marginTop: "auto",
  },
  spinner: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100dvh",
  },
  successIcon: {
    fontSize: 56,
    textAlign: "center",
    marginBottom: 8,
  },
  successText: {
    fontSize: 15,
    lineHeight: 1.5,
    color: "#333",
  },
  switchBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.4)",
    border: "none",
    color: "#fff",
    fontSize: 20,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
    WebkitTapHighlightColor: "transparent",
  },
  cancelBtn: {
    position: "absolute",
    top: 12,
    left: 12,
    padding: "8px 16px",
    borderRadius: 20,
    background: "rgba(0,0,0,0.4)",
    border: "none",
    color: "#fff",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    backdropFilter: "blur(4px)",
    WebkitTapHighlightColor: "transparent",
  },
};

export default function TryOnPage() {
  const query = useQuery();
  const shop = query.get("shop") || "";
  const productId = query.get("product") || "";
  const titleParam = query.get("title") || "";

  const [productInfo, setProductInfo] = useState(null);
  const [branding, setBranding] = useState(null);
  const [lang, setLang] = useState("nl");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [sidePhoto, setSidePhoto] = useState(null);
  const [sidePhotoPreview, setSidePhotoPreview] = useState(null);
  const [captureTarget, setCaptureTarget] = useState("front"); // "front" | "side"
  const captureTargetRef = useRef("front");
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState("user"); // default selfie camera for solo use with timer
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const pendingStreamRef = useRef(null); // holds stream until video element mounts
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const sideFileInputRef = useRef(null);

  const [email, setEmail] = useState("");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [fitPreference, setFitPreference] = useState("regular");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [progressStep, setProgressStep] = useState(0);

  // Timer for self-photo
  const TIMER_OPTIONS = [0, 3, 5, 10];
  const [timerSeconds, setTimerSeconds] = useState(5); // default 5s
  const [countdown, setCountdown] = useState(null); // null = not counting, number = seconds left
  const countdownRef = useRef(null);

  const isIframe = window.self !== window.top;

  // Load product
  useEffect(() => {
    if (!shop || !productId) {
      setError(translate(lang, "tryOn.invalidLink"));
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/widget/product-info?shop=${encodeURIComponent(shop)}&productId=${encodeURIComponent(productId)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) { setError(data.error); }
        else { setProductInfo(data); if (data.branding) setBranding(data.branding); if (data.language) setLang(data.language); }
      })
      .catch(() => setError(translate(lang, "tryOn.loadFailed")))
      .finally(() => setLoading(false));
  }, [shop, productId]);

  // Camera — acquire stream, then activate UI (video element mounts via state)
  const startCamera = useCallback(async (facing) => {
    // Stop existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const mode = facing || facingMode;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          aspectRatio: { ideal: 3 / 4 },
          width: { ideal: 1080 },
          height: { ideal: 1440 },
        },
        audio: false,
      });
      streamRef.current = stream;
      pendingStreamRef.current = stream;
      setCameraActive(true);
      setFacingMode(mode);
      // If video element already exists (e.g. switching camera), attach immediately
      requestAnimationFrame(() => {
        if (videoRef.current && pendingStreamRef.current) {
          videoRef.current.srcObject = pendingStreamRef.current;
          pendingStreamRef.current = null;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch {
      setError(translate(lang, "tryOn.cameraNotAvailable"));
    }
  }, [facingMode, lang]);

  // Attach stream to video element once it mounts (initial open)
  useEffect(() => {
    if (cameraActive && videoRef.current && pendingStreamRef.current) {
      const video = videoRef.current;
      const stream = pendingStreamRef.current;
      pendingStreamRef.current = null;
      video.srcObject = stream;
      video.play().catch(() => {});
    }
  }, [cameraActive]);

  const switchCamera = useCallback(() => {
    const newMode = facingMode === "user" ? "environment" : "user";
    startCamera(newMode);
  }, [facingMode, startCamera]);

  const cancelCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    cancelCountdown();
    setCameraActive(false);
  }, [cancelCountdown]);

  const doTakePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const vw = video.videoWidth || 1080;
    const vh = video.videoHeight || 1440;

    // Ensure we always produce a portrait (3:4) image.
    // Some phone cameras deliver landscape dimensions even when held upright.
    let srcX = 0, srcY = 0, srcW = vw, srcH = vh;
    const currentRatio = vw / vh;
    const targetRatio = 3 / 4; // portrait

    if (currentRatio > targetRatio) {
      // Video is too wide (landscape) — crop sides to get portrait
      srcW = Math.round(vh * targetRatio);
      srcX = Math.round((vw - srcW) / 2);
    } else if (currentRatio < targetRatio - 0.05) {
      // Video is taller than 3:4 — crop top/bottom slightly
      srcH = Math.round(vw / targetRatio);
      srcY = Math.round((vh - srcH) / 2);
    }

    canvas.width = srcW;
    canvas.height = srcH;
    const ctx = canvas.getContext("2d");
    if (facingMode === "user") {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const rawDataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    cancelCountdown();
    stopCamera();
    const { b64, dataUrl } = await compressImage(rawDataUrl);
    if (captureTargetRef.current === "side") {
      setSidePhoto(b64);
      setSidePhotoPreview(dataUrl);
    } else {
      setPhoto(b64);
      setPhotoPreview(dataUrl);
    }
  }, [stopCamera, facingMode, cancelCountdown]);

  const takePhoto = useCallback(() => {
    if (timerSeconds === 0) {
      doTakePhoto();
      return;
    }
    // Start countdown
    setCountdown(timerSeconds);
    let remaining = timerSeconds;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        setCountdown(null);
        doTakePhoto();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  }, [timerSeconds, doTakePhoto]);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be selected again
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = async () => {
      const { b64, dataUrl } = await compressImage(reader.result);
      if (captureTargetRef.current === "side") {
        setSidePhoto(b64);
        setSidePhotoPreview(dataUrl);
      } else {
        setPhoto(b64);
        setPhotoPreview(dataUrl);
      }
    };
    reader.readAsDataURL(file);
  }, []);

  const openCapture = useCallback((target, viaCamera) => {
    captureTargetRef.current = target;
    setCaptureTarget(target);
    if (viaCamera) startCamera();
    else if (target === "side") sideFileInputRef.current?.click();
    else fileInputRef.current?.click();
  }, [startCamera]);

  const resetPhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const resetSidePhoto = () => {
    setSidePhoto(null);
    setSidePhotoPreview(null);
  };

  const handleSubmit = async () => {
    if (!photo) return setError(translate(lang, "tryOn.takePhotoFirst"));
    const sizeAdviceOn = productInfo?.sizeAdviceEnabled !== false;
    if (sizeAdviceOn && (!height || !weight)) return setError(translate(lang, "tryOn.heightWeightRequired"));
    if (!email) return setError(translate(lang, "tryOn.enterEmail"));
    if (!privacyConsent) return setError(translate(lang, "tryOn.privacyConsentRequired"));
    setSubmitting(true);
    setProgressStep(0);
    setError(null);

    // Animate through progress steps while waiting
    const stepTimers = [];
    const stepDelays = [0, 3000, 8000, 20000, 35000];
    stepDelays.forEach((delay, i) => {
      stepTimers.push(setTimeout(() => setProgressStep(i), delay));
    });

    try {
      const res = await fetch(`${API_BASE}/widget/try-on`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shop, productId, customerEmail: email, customerPhotoB64: photo,
          customerSidePhotoB64: sidePhoto || undefined,
          customerHeight: height ? parseInt(height, 10) : undefined,
          customerWeight: weight ? parseInt(weight, 10) : undefined,
          customerAge: age ? parseInt(age, 10) : undefined,
          customerFitPreference: fitPreference !== "regular" ? fitPreference : undefined,
        }),
      });
      // Clear step timers
      stepTimers.forEach(clearTimeout);
      setProgressStep(4); // Final step

      if (res.status === 413) {
        throw new Error(translate(lang, "tryOn.photoTooBig"));
      }
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.error("Non-JSON response:", res.status, text.slice(0, 200));
        throw new Error(translate(lang, "tryOn.serverUnreachable"));
      }
      if (!res.ok) throw new Error(data.error || translate(lang, "common.error"));
      setResult(data);
    } catch (err) {
      stepTimers.forEach(clearTimeout);
      if (err.name === "TypeError" && err.message === "Failed to fetch") {
        setError(translate(lang, "tryOn.serverUnreachable"));
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isIframe) window.parent.postMessage("zyncfit-close", "*");
    else window.close();
  };

  useEffect(() => () => { stopCamera(); cancelCountdown(); }, [stopCamera, cancelCountdown]);

  const B = br(branding);

  // ── Loading ─────────────────────────────────
  if (loading) {
    return (
      <div style={styles.spinner}>
        <style>{`@keyframes zf-spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid ${B.primaryColor}`, borderTopColor: "transparent", animation: "zf-spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <div style={{ color: "#666", fontSize: 15, fontFamily: B.fontFamily }}>{translate(lang, "tryOn.loading")}</div>
        </div>
      </div>
    );
  }

  // ── Success ─────────────────────────────────
  if (result) {
    return (
      <div style={{ ...styles.page, fontFamily: B.fontFamily, color: B.textColor }}>
        <div style={{ textAlign: "center", marginTop: 8, marginBottom: 8 }}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" style={{ color: B.accentColor || "#10b981" }}>
            <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" fill={`${B.accentColor || "#10b981"}15`}/>
            <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h1 style={{ ...styles.title, textAlign: "center" }}>{translate(lang, "tryOn.success")}</h1>
        <div style={{ ...styles.card, borderRadius: parseInt(B.borderRadius) }}>
          {result.hasImage ? (
            <p style={styles.successText}>
              {translate(lang, "tryOn.resultSent")} <strong>{email}</strong>.
            </p>
          ) : result.imageError ? (
            <p style={{ fontSize: 14, color: "#b45309", background: "#fffbeb", padding: "12px", borderRadius: 8, margin: 0 }}>
              {translate(lang, "tryOn.imageTemporarilyUnavailable")}
            </p>
          ) : (
            <p style={styles.successText}>
              {translate(lang, "tryOn.resultSent")} <strong>{email}</strong>.
            </p>
          )}
          {/* Maatadvies direct tonen — incl. twijfelmaat en uitleg */}
          {result.recommendedSizes && Object.keys(result.recommendedSizes).length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eee" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#444", marginBottom: 8 }}>
                {translate(lang, "tryOn.yourSizes")}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(result.recommendedSizes).map(([garment, size]) => (
                  <div key={garment} style={{
                    flex: "1 1 90px",
                    textAlign: "center",
                    background: `${B.primaryColor}08`,
                    border: `1px solid ${B.primaryColor}20`,
                    borderRadius: 10,
                    padding: "10px 8px",
                  }}>
                    <div style={{ fontSize: 11, color: "#777", marginBottom: 2 }}>{translate(lang, `landing.${garment}`)}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: B.primaryColor }}>{size}</div>
                    {result.alternativeSizes?.[garment] && (
                      <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
                        {translate(lang, "tryOn.alternativeSizeHint", { alt: result.alternativeSizes[garment] })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {result.sizeAdjustments && Object.keys(result.sizeAdjustments).length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {[...new Set(Object.values(result.sizeAdjustments).flatMap(a =>
                    (a.applied || []).map(adj =>
                      adj.type === "customer_history" ? translate(lang, "tryOn.adjustedByHistory")
                      : adj.type === "vendor_bias" ? translate(lang, adj.direction === "up" ? "tryOn.adjustedByBrandUp" : "tryOn.adjustedByBrandDown")
                      : adj.type === "fit_preference" ? translate(lang, "tryOn.adjustedByFit")
                      : null
                    ).filter(Boolean)
                  ))].map((line, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, color: "#666", padding: "3px 0", lineHeight: 1.4 }}>
                      <span style={{ color: B.accentColor || "#10b981", flexShrink: 0 }}>✓</span>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              )}
              {result.fitNotes?.garments && (() => {
                const tipKeyMap = {
                  athletic_jacket: "tryOn.fitTipAthleticJacket",
                  athletic_jacket_option: "tryOn.fitTipAthleticJacketOption",
                  fuller_jacket: "tryOn.fitTipFullerJacket",
                  between_jacket: "tryOn.fitTipBetweenJacket",
                  between_trousers: "tryOn.fitTipBetweenTrousers",
                  trousers_alter: "tryOn.fitTipTrousersAlter",
                  between_gilet: "tryOn.fitTipBetweenGilet",
                };
                const lines = Object.entries(result.fitNotes.garments).map(([g, n]) => {
                  const key = tipKeyMap[n.tip];
                  if (!key) return null;
                  return translate(lang, key, { size: result.recommendedSizes?.[g] || "", alt: n.alt || "" });
                }).filter(Boolean);
                const build = result.fitNotes.build === "athletic" ? translate(lang, "tryOn.fitBuildAthletic", { drop: result.fitNotes.drop })
                  : result.fitNotes.build === "fuller" ? translate(lang, "tryOn.fitBuildFuller", { drop: result.fitNotes.drop }) : null;
                if (!build && lines.length === 0) return null;
                return (
                  <div style={{ marginTop: 10, background: `${B.primaryColor}06`, borderRadius: 10, padding: "10px 12px" }}>
                    {build && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: B.primaryColor, marginBottom: lines.length ? 6 : 0, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        💪 {build}
                      </div>
                    )}
                    {lines.map((line, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, color: "#555", padding: "3px 0", lineHeight: 1.5 }}>
                        <span style={{ flexShrink: 0 }}>✂️</span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
          {result.emailSent === false && result.landingPageUrl && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 13, color: "#b45309", background: "#fffbeb", padding: "10px 12px", borderRadius: 8, margin: "0 0 8px" }}>
                {translate(lang, "tryOn.emailFailed")}
              </p>
              <a
                href={result.landingPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "12px 16px",
                  background: `${B.primaryColor}10`,
                  color: B.primaryColor,
                  borderRadius: parseInt(B.borderRadius) * 0.5,
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                {translate(lang, "tryOn.openLandingDirect")}
              </a>
            </div>
          )}
        </div>
        <button style={{ ...styles.btnPrimary, background: B.buttonStyle === "solid" ? B.primaryColor : B.buttonStyle === "outline" ? "transparent" : `linear-gradient(135deg, ${B.primaryColor}, ${B.secondaryColor})`, color: B.buttonStyle === "outline" ? B.primaryColor : "#fff", border: B.buttonStyle === "outline" ? `2px solid ${B.primaryColor}` : "none", borderRadius: parseInt(B.borderRadius) * 0.6 }} onClick={handleClose}>
          {translate(lang, "tryOn.backToShop")}
        </button>
        <div style={styles.footer}>{translate(lang, "common.poweredBy")}</div>
      </div>
    );
  }

  // ── Camera fullscreen — futuristic body scanner ──
  if (cameraActive) {
    const guideText = captureTarget === "side"
      ? translate(lang, "tryOn.cameraGuideSide")
      : facingMode === "user"
        ? translate(lang, "tryOn.cameraGuideSelf")
        : translate(lang, "tryOn.cameraGuideOther");

    return (
      <div style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        <style>{`
          @keyframes zf-scanline {
            0% { top: 5%; }
            50% { top: 90%; }
            100% { top: 5%; }
          }
          @keyframes zf-dot-pulse {
            0%, 100% { opacity: 0.3; r: 2; }
            50% { opacity: 1; r: 3.5; }
          }
          @keyframes zf-corner-glow {
            0%, 100% { filter: drop-shadow(0 0 2px #00ff88); opacity: 0.7; }
            50% { filter: drop-shadow(0 0 6px #00ff88) drop-shadow(0 0 12px #00ff44); opacity: 1; }
          }
          @keyframes zf-data-scroll {
            0% { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }
          @keyframes zf-grid-pulse {
            0%, 100% { opacity: 0.04; }
            50% { opacity: 0.08; }
          }
          @keyframes zf-outline-trace {
            0% { stroke-dashoffset: 1200; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes zf-countdown-pop {
            0% { transform: translate(-50%,-50%) scale(0.3); opacity: 0; }
            40% { transform: translate(-50%,-50%) scale(1.15); opacity: 1; }
            100% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
          }
          @keyframes zf-countdown-ring {
            0% { stroke-dashoffset: 283; }
            100% { stroke-dashoffset: 0; }
          }
        `}</style>

        {/* Video feed */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            flex: 1,
            width: "100%",
            objectFit: "cover",
            transform: facingMode === "user" ? "scaleX(-1)" : "none",
          }}
        />

        {/* ── Scanner overlay ── */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>

          {/* Subtle grid pattern */}
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage: "linear-gradient(rgba(0,255,100,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,100,0.06) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            animation: "zf-grid-pulse 4s ease-in-out infinite",
          }} />

          {/* Moving scan line */}
          <div style={{
            position: "absolute",
            left: "8%", right: "8%",
            height: 2,
            animation: "zf-scanline 3.5s ease-in-out infinite",
            background: "linear-gradient(90deg, transparent, #00ff88, #00ffaa, #00ff88, transparent)",
            boxShadow: "0 0 15px 3px rgba(0,255,136,0.5), 0 0 40px 8px rgba(0,255,136,0.15)",
            borderRadius: 2,
          }} />

          {/* Corner brackets — futuristic frame */}
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", animation: "zf-corner-glow 3s ease-in-out infinite" }}>
            {/* Top-left */}
            <path d="M45,30 L25,30 L25,60" fill="none" stroke="#00ff88" strokeWidth="2.5" strokeLinecap="round" />
            {/* Top-right */}
            <path d="M-45,30 L-25,30 L-25,60" fill="none" stroke="#00ff88" strokeWidth="2.5" strokeLinecap="round" transform={`translate(${typeof window !== 'undefined' ? window.innerWidth : 400}, 0) scale(-1,1)`} />
            {/* Bottom-left */}
            <path d="M45,-30 L25,-30 L25,-60" fill="none" stroke="#00ff88" strokeWidth="2.5" strokeLinecap="round" transform={`translate(0, ${typeof window !== 'undefined' ? window.innerHeight : 800}) scale(1,-1)`} />
            {/* Bottom-right */}
            <path d="M45,-30 L25,-30 L25,-60" fill="none" stroke="#00ff88" strokeWidth="2.5" strokeLinecap="round" transform={`translate(${typeof window !== 'undefined' ? window.innerWidth : 400}, ${typeof window !== 'undefined' ? window.innerHeight : 800}) scale(-1,-1)`} />
          </svg>

          {/* Body outline with scanning dots */}
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg
              viewBox="0 0 200 500"
              preserveAspectRatio="xMidYMid meet"
              style={{ height: "82%", maxHeight: "80vh" }}
            >
              {/* Body outline — neon green traced lines */}
              <ellipse cx="100" cy="38" rx="22" ry="28" fill="none" stroke="#00ff88" strokeWidth="1" strokeDasharray="600" style={{ animation: "zf-outline-trace 2s ease-out forwards", opacity: 0.5 }} />
              <path d="M78,64 L78,80 Q48,82 46,90 Q42,135 48,190 Q52,218 58,240 Q62,320 68,390 Q72,430 75,470" fill="none" stroke="#00ff88" strokeWidth="0.8" strokeDasharray="600" style={{ animation: "zf-outline-trace 3s ease-out forwards", opacity: 0.4 }} />
              <path d="M122,64 L122,80 Q152,82 154,90 Q158,135 152,190 Q148,218 142,240 Q138,320 132,390 Q128,430 125,470" fill="none" stroke="#00ff88" strokeWidth="0.8" strokeDasharray="600" style={{ animation: "zf-outline-trace 3s ease-out forwards", opacity: 0.4 }} />

              {/* Measurement lines — green dashed */}
              <line x1="46" y1="90" x2="154" y2="90" stroke="#00ff88" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.4" />
              <line x1="48" y1="150" x2="152" y2="150" stroke="#00ff88" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.3" />
              <line x1="50" y1="190" x2="150" y2="190" stroke="#00ff88" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.4" />
              <line x1="50" y1="218" x2="150" y2="218" stroke="#00ff88" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.3" />

              {/* Scanning dots at key body points */}
              {[
                [100,10], [78,38], [122,38], // head
                [100,70], // neck
                [46,90], [154,90], [100,90], // shoulders
                [48,150], [152,150], // chest
                [50,190], [150,190], // waist
                [50,218], [150,218], // hips
                [62,300], [138,300], // thighs
                [66,360], [134,360], // knees
                [72,430], [128,430], // calves
                [75,470], [125,470], // ankles
              ].map(([cx, cy], i) => (
                <circle
                  key={i} cx={cx} cy={cy} r="2"
                  fill="#00ff88"
                  style={{
                    animation: `zf-dot-pulse 1.5s ease-in-out ${i * 0.12}s infinite`,
                    filter: "drop-shadow(0 0 3px #00ff88)",
                  }}
                />
              ))}

              {/* Measurement labels — neon style */}
              <text x="160" y="92" fill="#00ff88" fontSize="7" fontFamily="monospace" opacity="0.7">{translate(lang, "tryOn.guideShoulders")}</text>
              <text x="156" y="152" fill="#00ff88" fontSize="7" fontFamily="monospace" opacity="0.6">{translate(lang, "tryOn.guideChest")}</text>
              <text x="155" y="192" fill="#00ff88" fontSize="7" fontFamily="monospace" opacity="0.7">{translate(lang, "tryOn.guideWaist")}</text>
              <text x="155" y="220" fill="#00ff88" fontSize="7" fontFamily="monospace" opacity="0.6">{translate(lang, "tryOn.guideHips")}</text>
            </svg>
          </div>

          {/* Left-side data readout */}
          <div style={{
            position: "absolute",
            top: 100, left: 10, bottom: 150,
            width: 55,
            overflow: "hidden",
            opacity: 0.35,
          }}>
            <div style={{
              fontFamily: "monospace",
              fontSize: 8,
              color: "#00ff88",
              lineHeight: 1.6,
              animation: "zf-data-scroll 12s linear infinite",
            }}>
              {["SCAN_INIT", "BODY_DET", "POSE_EST", "CHEST: --", "WAIST: --", "HIPS: --", "SHLD: --", "ARM_L: --", "INSEAM: --", "NECK: --", "THIGH: --", "KNEE: --", "CALIBR...", "HEIGHT: --", "WEIGHT: --", "CONF: MED",
                "SCAN_INIT", "BODY_DET", "POSE_EST", "CHEST: --", "WAIST: --", "HIPS: --", "SHLD: --", "ARM_L: --", "INSEAM: --", "NECK: --", "THIGH: --", "KNEE: --", "CALIBR...", "HEIGHT: --", "WEIGHT: --", "CONF: MED",
              ].map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>

          {/* Right-side data readout */}
          <div style={{
            position: "absolute",
            top: 120, right: 10, bottom: 150,
            width: 55,
            overflow: "hidden",
            opacity: 0.3,
          }}>
            <div style={{
              fontFamily: "monospace",
              fontSize: 8,
              color: "#00ff88",
              lineHeight: 1.6,
              textAlign: "right",
              animation: "zf-data-scroll 15s linear infinite",
            }}>
              {["ZYNCFIT", "v3.2.1", "━━━━━━", "DIST_OK", "LIGHT_OK", "POSE_OK", "FRAME_OK", "RES 1080", "━━━━━━", "MEASURE", "CALIBR.", "ANALYZE", "MATCH..", "PROCESS",
                "ZYNCFIT", "v3.2.1", "━━━━━━", "DIST_OK", "LIGHT_OK", "POSE_OK", "FRAME_OK", "RES 1080", "━━━━━━", "MEASURE", "CALIBR.", "ANALYZE", "MATCH..", "PROCESS",
              ].map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Top banner — scanner mode */}
        <div style={{
          position: "absolute",
          top: 60,
          left: 0, right: 0,
          textAlign: "center",
          pointerEvents: "none",
          zIndex: 10,
        }}>
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(10px)",
            color: "#00ff88",
            padding: "8px 20px",
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "monospace",
            maxWidth: "90%",
            border: "1px solid rgba(0,255,136,0.2)",
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "#00ff88",
              boxShadow: "0 0 6px #00ff88",
              animation: "zf-dot-pulse 1.5s ease-in-out infinite",
            }} />
            {guideText}
          </div>
        </div>

        {/* Distance indicator — scanner style */}
        <div style={{
          position: "absolute",
          bottom: 112,
          left: 0, right: 0,
          textAlign: "center",
          pointerEvents: "none",
          zIndex: 10,
        }}>
          <div style={{
            display: "inline-block",
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(6px)",
            color: "#00ffaa",
            padding: "5px 14px",
            borderRadius: 12,
            fontSize: 10,
            fontWeight: 600,
            fontFamily: "monospace",
            border: "1px solid rgba(0,255,136,0.15)",
          }}>
            ◎ {translate(lang, "tryOn.distanceTip")}
          </div>
        </div>

        {/* Countdown overlay */}
        {countdown !== null && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 20,
            pointerEvents: "none",
          }}>
            <div key={countdown} style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              animation: "zf-countdown-pop 0.4s ease-out",
            }}>
              <svg width="140" height="140" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="rgba(0,0,0,0.5)" />
                <circle
                  cx="50" cy="50" r="45"
                  fill="none"
                  stroke="#00ff88"
                  strokeWidth="3"
                  strokeDasharray="283"
                  strokeDashoffset="283"
                  strokeLinecap="round"
                  transform="rotate(-90 50 50)"
                  style={{ animation: "zf-countdown-ring 1s linear forwards" }}
                />
                <text x="50" y="56" textAnchor="middle" fontSize="38" fontWeight="700" fill="#00ff88" fontFamily="monospace">{countdown}</text>
              </svg>
            </div>
          </div>
        )}

        {/* Cancel */}
        <button style={{ ...styles.cancelBtn, color: "#00ff88", background: "rgba(0,20,10,0.6)", border: "1px solid rgba(0,255,136,0.3)", fontFamily: "monospace", fontSize: 12 }} onClick={stopCamera}>
          ✕ {translate(lang, "tryOn.cancelCamera")}
        </button>
        {/* Switch camera */}
        <button style={{ ...styles.switchBtn, background: "rgba(0,20,10,0.6)", border: "1px solid rgba(0,255,136,0.3)", color: "#00ff88" }} onClick={switchCamera}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
        </button>
        {/* Timer toggle button */}
        <button
          style={{
            position: "absolute",
            top: 12,
            right: 60,
            height: 40,
            borderRadius: 20,
            background: timerSeconds > 0 ? "rgba(0,40,20,0.7)" : "rgba(0,20,10,0.6)",
            border: `1px solid ${timerSeconds > 0 ? "rgba(0,255,136,0.5)" : "rgba(0,255,136,0.2)"}`,
            color: "#00ff88",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "monospace",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "0 12px",
            backdropFilter: "blur(4px)",
            WebkitTapHighlightColor: "transparent",
          }}
          onClick={() => {
            if (countdown !== null) return;
            const idx = TIMER_OPTIONS.indexOf(timerSeconds);
            setTimerSeconds(TIMER_OPTIONS[(idx + 1) % TIMER_OPTIONS.length]);
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2"/><path d="M5 3l-2 2"/><path d="M19 3l2 2"/><path d="M12 1v2"/></svg>
          {timerSeconds > 0 ? `${timerSeconds}s` : translate(lang, "tryOn.timerOff")}
        </button>
        {/* Capture button — scanner style */}
        <div style={{
          position: "absolute",
          bottom: 28,
          left: 0, right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 10,
        }}>
          {countdown !== null ? (
            <button style={{
              width: 72, height: 72, borderRadius: "50%",
              border: "3px solid #ff4444",
              background: "rgba(40,0,0,0.6)",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 15px rgba(255,68,68,0.4)",
              WebkitTapHighlightColor: "transparent",
              touchAction: "manipulation",
            }} onClick={cancelCountdown}>
              <div style={{ width: 26, height: 26, borderRadius: 4, background: "#ff4444" }} />
            </button>
          ) : (
            <button style={{
              width: 72, height: 72, borderRadius: "50%",
              border: "3px solid #00ff88",
              background: "rgba(0,20,10,0.5)",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 15px rgba(0,255,136,0.3), inset 0 0 15px rgba(0,255,136,0.1)",
              WebkitTapHighlightColor: "transparent",
              touchAction: "manipulation",
            }} onClick={takePhoto}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: "radial-gradient(circle, rgba(0,255,136,0.3), rgba(0,255,136,0.1))",
                border: "2px solid rgba(0,255,136,0.5)",
              }} />
            </button>
          )}
        </div>
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
    );
  }

  // ── Processing animation ─────────────────────
  if (submitting) {
    const STEP_ICONS = [
      // step 0: analyzing photo
      <svg key="s0" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={B.primaryColor} strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
      // step 1: fitting garment
      <svg key="s1" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={B.primaryColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.47a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.47a2 2 0 00-1.34-2.23z"/></svg>,
      // step 2: body measurements
      <svg key="s2" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={B.primaryColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h5"/><path d="M17 12h5"/><path d="M7 4v16"/><path d="M17 4v16"/><path d="M7 8h10"/><path d="M7 16h10"/></svg>,
      // step 3: finding size
      <svg key="s3" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={B.primaryColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
      // step 4: preparing result
      <svg key="s4" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={B.accentColor || "#10b981"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    ];
    const STEP_KEYS = ["tryOn.step1", "tryOn.step2", "tryOn.step3", "tryOn.step4", "tryOn.step5"];

    return (
      <div style={{ ...styles.page, fontFamily: B.fontFamily, color: B.textColor, justifyContent: "center", alignItems: "center" }}>
        <style>{`
          @keyframes zf-fade-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes zf-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.08); opacity: 0.85; } }
          @keyframes zf-progress { from { width: 0%; } to { width: 100%; } }
          @keyframes zf-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        `}</style>

        {/* Product image as context */}
        {productInfo?.garmentImageUrl && (
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <img
              src={productInfo.garmentImageUrl}
              alt=""
              style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 12, opacity: 0.6 }}
            />
          </div>
        )}

        {/* Animated icon */}
        <div style={{ textAlign: "center", marginBottom: 20, animation: "zf-pulse 2s ease-in-out infinite" }}>
          {STEP_ICONS[progressStep] || STEP_ICONS[0]}
        </div>

        {/* Steps list */}
        <div style={{
          background: "#fff",
          borderRadius: parseInt(B.borderRadius),
          padding: "24px 20px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          width: "100%",
          maxWidth: 360,
        }}>
          {STEP_KEYS.map((key, i) => {
            const isActive = i === progressStep;
            const isDone = i < progressStep;
            const isFuture = i > progressStep;
            return (
              <div
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom: i < STEP_KEYS.length - 1 ? "1px solid #f0f0f0" : "none",
                  opacity: isFuture ? 0.35 : 1,
                  transition: "opacity 0.5s ease",
                  ...(isActive ? { animation: "zf-fade-in 0.4s ease" } : {}),
                }}
              >
                {/* Status indicator */}
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  background: isDone ? (B.accentColor || "#10b981") : isActive ? B.primaryColor : "#e5e5e5",
                  transition: "background 0.3s ease",
                }}>
                  {isDone ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  ) : isActive ? (
                    <div style={{
                      width: 10, height: 10, borderRadius: "50%",
                      border: "2px solid #fff", borderTopColor: "transparent",
                      animation: "zf-spin 0.8s linear infinite",
                    }} />
                  ) : (
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ccc" }} />
                  )}
                </div>

                {/* Step text */}
                <span style={{
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 400,
                  color: isDone ? (B.accentColor || "#10b981") : isActive ? B.textColor : "#999",
                  transition: "color 0.3s ease",
                }}>
                  {translate(lang, key)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Shimmer progress bar */}
        <div style={{
          width: "100%",
          maxWidth: 360,
          height: 4,
          borderRadius: 2,
          background: "#eee",
          marginTop: 20,
          overflow: "hidden",
        }}>
          <div style={{
            height: "100%",
            borderRadius: 2,
            background: `linear-gradient(90deg, ${B.primaryColor}, ${B.secondaryColor}, ${B.primaryColor})`,
            backgroundSize: "200% 100%",
            animation: "zf-shimmer 1.5s ease-in-out infinite",
            width: `${((progressStep + 1) / STEP_KEYS.length) * 100}%`,
            transition: "width 0.6s ease",
          }} />
        </div>

        <p style={{ color: "#999", fontSize: 12, textAlign: "center", marginTop: 16 }}>
          {translate(lang, "common.poweredBy")}
        </p>

        <style>{`@keyframes zf-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── Main page ───────────────────────────────
  const productTitle = productInfo?.productTitle || titleParam;
  const radius = parseInt(B.borderRadius);
  const btnRadius = Math.round(radius * 0.6);
  const primaryBtnStyle = {
    ...styles.btnPrimary,
    background: B.buttonStyle === "solid" ? B.primaryColor
      : B.buttonStyle === "outline" ? "transparent"
      : `linear-gradient(135deg, ${B.primaryColor}, ${B.secondaryColor})`,
    color: B.buttonStyle === "outline" ? B.primaryColor : "#fff",
    border: B.buttonStyle === "outline" ? `2px solid ${B.primaryColor}` : "none",
    borderRadius: btnRadius,
    fontFamily: B.fontFamily,
  };

  return (
    <div style={{ ...styles.page, fontFamily: B.fontFamily, color: B.textColor }}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{translate(lang, "tryOn.title")}</h1>
        {productTitle && <p style={styles.subtitle}>{productTitle}</p>}
      </div>

      {error && (
        <div style={{ ...styles.error, borderRadius: btnRadius }} onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {/* Product image (compact) */}
      {productInfo?.garmentImageUrl && !photo && (
        <div style={{ ...styles.card, borderRadius: radius }}>
          <span style={styles.label}>{translate(lang, "tryOn.thisArticle")}</span>
          <img
            src={productInfo.garmentImageUrl}
            alt={productTitle}
            style={{ ...styles.productImg, borderRadius: radius * 0.75 }}
          />
        </div>
      )}

      {/* Photo section */}
      {photo ? (
        <div style={{ ...styles.card, borderRadius: radius }}>
          <span style={styles.label}>{translate(lang, "tryOn.yourPhoto")}</span>
          <img src={photoPreview} alt={translate(lang, "tryOn.yourPhoto")} style={{ ...styles.photoPreview, borderRadius: radius * 0.75 }} />
          <button style={{ ...styles.btnText, marginTop: 8, color: B.primaryColor, fontFamily: B.fontFamily }} onClick={resetPhoto}>
            {translate(lang, "tryOn.chooseOtherPhoto")}
          </button>
        </div>
      ) : (
        <div style={{ ...styles.card, borderRadius: radius }}>
          <span style={styles.label}>{translate(lang, "tryOn.takePhotoLabel")}</span>
          <p style={{ fontSize: 13, color: "#888", margin: "0 0 12px", lineHeight: 1.4 }}>
            {translate(lang, "tryOn.takePhotoHelp")}
          </p>

          {/* Photo tips — measurement-focused */}
          <div style={{
            background: `${B.primaryColor}08`,
            borderRadius: btnRadius,
            padding: "12px 14px",
            marginBottom: 16,
            border: `1px solid ${B.primaryColor}15`,
          }}>
            {["photoTip1", "photoTip2", "photoTip3", "photoTip4", "photoTip5", "photoTip6"].map((tipKey, i) => {
              const tip = translate(lang, `tryOn.${tipKey}`);
              if (!tip || tip === `tryOn.${tipKey}`) return null;
              return (
                <div key={tipKey} style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "4px 0",
                  fontSize: 12,
                  color: "#555",
                  lineHeight: 1.4,
                }}>
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: B.primaryColor,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: 1,
                  }}>
                    {i + 1}
                  </span>
                  <span>{tip}</span>
                </div>
              );
            })}
          </div>

          <button style={primaryBtnStyle} onClick={() => openCapture("front", true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
            {translate(lang, "tryOn.openCamera")}
          </button>
          <div style={styles.divider}>{translate(lang, "common.or")}</div>
          <button style={{ ...styles.btnSecondary, background: `${B.primaryColor}10`, color: B.primaryColor, borderRadius: btnRadius, fontFamily: B.fontFamily }} onClick={() => openCapture("front", false)}>
            {translate(lang, "tryOn.uploadPhoto")}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
        </div>
      )}

      {/* Body info + Email + submit */}
      {photo && (
        <div style={{ ...styles.card, borderRadius: radius }}>
          {/* Side photo (optional, improves accuracy) */}
          <input ref={sideFileInputRef} type="file" accept="image/*" onChange={handleFileUpload} style={{ display: "none" }} />
          <span style={styles.label}>{translate(lang, "tryOn.sidePhotoLabel")}</span>
          <p style={{ fontSize: 12, color: "#888", margin: "0 0 10px", lineHeight: 1.4 }}>
            {translate(lang, "tryOn.sidePhotoHelp")}
          </p>
          {sidePhoto ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <img src={sidePhotoPreview} alt="" style={{ width: 56, height: 74, objectFit: "cover", borderRadius: btnRadius }} />
              <button style={{ ...styles.btnText, color: "#b91c1c", fontFamily: B.fontFamily, padding: 0 }} onClick={resetSidePhoto}>
                {translate(lang, "tryOn.removeSidePhoto")}
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button style={{ ...styles.btnSecondary, flex: 1, background: `${B.primaryColor}10`, color: B.primaryColor, borderRadius: btnRadius, fontFamily: B.fontFamily, fontSize: 13, padding: "10px 8px" }} onClick={() => openCapture("side", true)}>
                {translate(lang, "tryOn.addSidePhotoCamera")}
              </button>
              <button style={{ ...styles.btnSecondary, flex: 1, background: `${B.primaryColor}10`, color: B.primaryColor, borderRadius: btnRadius, fontFamily: B.fontFamily, fontSize: 13, padding: "10px 8px" }} onClick={() => openCapture("side", false)}>
                {translate(lang, "tryOn.addSidePhotoUpload")}
              </button>
            </div>
          )}

          {productInfo?.sizeAdviceEnabled !== false && (<>
          <span style={styles.label}>{translate(lang, "tryOn.bodyInfoLabel")}</span>
          <p style={{ fontSize: 12, color: "#888", margin: "0 0 12px", lineHeight: 1.4 }}>
            {translate(lang, "tryOn.bodyInfoHelp")}
          </p>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: "#666", marginBottom: 3, display: "block" }}>{translate(lang, "tryOn.heightLabel")} <span style={{ color: "#dc2626" }}>*</span></label>
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="175"
                style={{ ...styles.input, borderRadius: btnRadius, fontFamily: B.fontFamily, padding: "12px 12px" }}
                inputMode="numeric"
                min="100" max="230"
                required
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: "#666", marginBottom: 3, display: "block" }}>{translate(lang, "tryOn.weightLabel")} <span style={{ color: "#dc2626" }}>*</span></label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="75"
                style={{ ...styles.input, borderRadius: btnRadius, fontFamily: B.fontFamily, padding: "12px 12px" }}
                inputMode="numeric"
                min="30" max="250"
                required
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: "#666", marginBottom: 3, display: "block" }}>{translate(lang, "tryOn.ageLabel")}</label>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="30"
                style={{ ...styles.input, borderRadius: btnRadius, fontFamily: B.fontFamily, padding: "12px 12px" }}
                inputMode="numeric"
                min="10" max="120"
              />
            </div>
          </div>

          {/* Fit preference */}
          <label style={{ fontSize: 11, color: "#666", marginBottom: 4, display: "block" }}>{translate(lang, "tryOn.fitPreferenceLabel")}</label>
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {["slim", "regular", "comfort"].map((pref) => (
              <button
                key={pref}
                onClick={() => setFitPreference(pref)}
                style={{
                  flex: 1,
                  padding: "10px 6px",
                  fontSize: 13,
                  fontWeight: fitPreference === pref ? 700 : 500,
                  fontFamily: B.fontFamily,
                  borderRadius: btnRadius,
                  border: fitPreference === pref ? `2px solid ${B.primaryColor}` : "1px solid #ddd",
                  background: fitPreference === pref ? `${B.primaryColor}12` : "#fff",
                  color: fitPreference === pref ? B.primaryColor : "#555",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {translate(lang, pref === "slim" ? "tryOn.fitSlim" : pref === "comfort" ? "tryOn.fitComfort" : "tryOn.fitRegular")}
              </button>
            ))}
          </div>
          </>)}

          <span style={styles.label}>{translate(lang, "tryOn.emailLabel")}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={translate(lang, "tryOn.emailPlaceholder")}
            style={{ ...styles.input, borderRadius: btnRadius, fontFamily: B.fontFamily }}
            autoComplete="email"
            inputMode="email"
          />
          <p style={{ fontSize: 12, color: "#999", margin: "6px 0 16px" }}>
            {translate(lang, "tryOn.emailHelp")}
          </p>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 16, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={privacyConsent}
              onChange={(e) => setPrivacyConsent(e.target.checked)}
              style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, accentColor: B.primaryColor }}
            />
            <span style={{ fontSize: 12, color: "#666", lineHeight: 1.5 }}>
              {translate(lang, "tryOn.privacyConsent")}{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: B.primaryColor, textDecoration: "underline" }}>
                {translate(lang, "tryOn.privacyPolicyLink")}
              </a>
            </span>
          </label>
          <button
            style={{
              ...primaryBtnStyle,
              opacity: (!email || !privacyConsent || (productInfo?.sizeAdviceEnabled !== false && (!height || !weight))) ? 0.6 : 1,
            }}
            onClick={handleSubmit}
            disabled={!email || !privacyConsent || (productInfo?.sizeAdviceEnabled !== false && (!height || !weight))}
          >
            {translate(lang, "tryOn.submit")}
          </button>
        </div>
      )}

      <div style={styles.footer}>{translate(lang, "common.poweredBy")}</div>
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
